import { UppyFile } from '@uppy/core';
import {
  AddUploadsToReportMutation,
  UploadSource,
  useAddReportDocumentationUploadMutation,
  useAddUploadsToReportMutation,
  useCreateReportDocumentationUploadUrlMutation,
} from 'src/graphql';
import { uploadToPresignedUrl } from 'src/utils/uploadToPresignedUrl';
import { getFileExtension } from 'src/utils/validateFileUpload/fileValidation';
import { useFileUploadContext } from '../FileUploadContext';

// Hook for reporter-facing file uploads
export const useUploadReportDocumentation = ({
  uploadSource,
  onPostSubmissionUploadSuccess,
}: {
  uploadSource: UploadSource;
  onPostSubmissionUploadSuccess?: (
    uploads?: AddUploadsToReportMutation['addUploadsToReport'],
  ) => void;
}) => {
  const createReportDocumentationUploadUrl =
    useCreateReportDocumentationUploadUrlMutation();
  const addReportDocumentationUpload =
    useAddReportDocumentationUploadMutation();
  const addUploadsToReport = useAddUploadsToReportMutation({
    onSuccess: (res) => {
      onPostSubmissionUploadSuccess?.(res.addUploadsToReport);
    },
  });

  const { handleUploadErrors, handleUploadRequestStateChange } =
    useFileUploadContext();

  const uploadFile = async ({
    file,
    reportId,
    eventId,
  }: {
    file: UppyFile;
    reportId: string;
    eventId: string;
  }) => {
    if (!file.type) {
      throw new Error('File type not found');
    }

    const {
      createReportDocumentationUploadUrl: { s3path, url: s3url },
    } = await createReportDocumentationUploadUrl.mutateAsync({
      fileType: file.type,
      reportId,
      uploadSource,
      filename: file.name,
      size: file.size,
    });

    await uploadToPresignedUrl({
      url: s3url,
      file: file.data,
      tags: { uploadSource },
      filename: file.name,
      mimeType: file.type,
    });

    await addReportDocumentationUpload.mutateAsync({
      documentation: {
        s3path,
        filename: file.name,
        size: file.size,
        mimeType: file.type,
      },
      report: reportId,
      event: eventId,
      uploadSource,
    });
  };

  const uploadFilesToReportPostSubmission = async ({
    files,
    reportId,
  }: {
    files: UppyFile[];
    reportId: string;
  }) => {
    handleUploadRequestStateChange(true);
    let errorsCount = 0;
    const successfulFileUploads = {
      uploads: [] as {
        s3path: string;
        size: number;
        mimeType: string;
        fileExtension: string;
      }[],
      files: [] as UppyFile[],
    };

    await Promise.all(
      files.map(async (file) => {
        if (!file.type) {
          throw new Error('File type not found');
        }

        const {
          createReportDocumentationUploadUrl: { s3path, url: s3url },
        } = await createReportDocumentationUploadUrl.mutateAsync({
          fileType: file.type,
          reportId,
          uploadSource,
          filename: file.name,
          size: file.size,
        });

        try {
          await uploadToPresignedUrl({
            url: s3url,
            file: file.data,
            tags: { uploadSource },
            filename: file.name,
            mimeType: file.type,
          });

          successfulFileUploads.uploads.push({
            s3path,
            size: file.size,
            mimeType: file.type,
            fileExtension: file.extension || getFileExtension(file.name),
          });
          successfulFileUploads.files.push(file);
        } catch (error) {
          errorsCount++;
        }
      }),
    );

    if (successfulFileUploads.uploads.length > 0) {
      await addUploadsToReport.mutateAsync({
        uploads: successfulFileUploads.uploads,
        report: reportId,
        uploadSource,
      });
    }

    handleUploadRequestStateChange(false);
    handleUploadErrors(errorsCount);

    return {
      successfulFileUploads: successfulFileUploads.files,
    };
  };

  return { uploadFile, uploadFilesToReportPostSubmission };
};
